import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCashRegister, faChartLine, faClock, faDownload, faEuroSign, faEye, faFilter, faUser } from '@fortawesome/free-solid-svg-icons';

const DailyRevenuePage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState('2024-11-15');
  const [filterStaff, setFilterStaff] = useState('all');

  const breadcrumb = [{ label: 'Financování' }, { label: 'Denní tržby' }];

  const staff = [
    { id: '1', name: 'Anna Svobodová', role: 'Kadeřnice' },
    { id: '2', name: 'Petr Novák', role: 'Kadeřník' },
    { id: '3', name: 'Marie Krásná', role: 'Kadeřnice' },
    { id: '4', name: 'Jana Nová', role: 'Kosmetička' },
  ];

  const dailyRevenue = [
    {
      id: '1',
      staffId: '1',
      customer: 'Jana Nováková',
      service: 'Střih a foukaná',
      time: '10:30',
      duration: 60,
      price: 800,
      paymentMethod: 'hotovost',
      status: 'completed',
    },
    {
      id: '2',
      staffId: '2',
      customer: 'Petr Svoboda',
      service: 'Holení',
      time: '11:00',
      duration: 30,
      price: 400,
      paymentMethod: 'karta',
      status: 'completed',
    },
    {
      id: '3',
      staffId: '3',
      customer: 'Marie Krásná',
      service: 'Barvení',
      time: '11:30',
      duration: 120,
      price: 1500,
      paymentMethod: 'karta',
      status: 'completed',
    },
    {
      id: '4',
      staffId: '4',
      customer: 'Alena Světlá',
      service: 'Kosmetické ošetření',
      time: '12:00',
      duration: 90,
      price: 1200,
      paymentMethod: 'převod',
      status: 'completed',
    },
    {
      id: '5',
      staffId: '1',
      customer: 'Lukáš Černý',
      service: 'Střih',
      time: '13:00',
      duration: 45,
      price: 600,
      paymentMethod: 'hotovost',
      status: 'completed',
    },
    {
      id: '6',
      staffId: '2',
      customer: 'Tomáš Vlasák',
      service: 'Úprava vousů',
      time: '14:00',
      duration: 30,
      price: 350,
      paymentMethod: 'karta',
      status: 'completed',
    },
  ];

  const dailyStats = {
    totalRevenue: 4850,
    totalServices: 6,
    averageService: 808,
    paymentMethods: {
      hotovost: 1400,
      karta: 2450,
      prevod: 1000,
    },
    staffRevenue: {
      '1': 1400,
      '2': 750,
      '3': 1500,
      '4': 1200,
    },
  };

  const filteredRevenue = dailyRevenue.filter(revenue => filterStaff === 'all' || revenue.staffId === filterStaff);

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'hotovost':
        return faCashRegister;
      case 'karta':
        return faEuroSign;
      case 'prevod':
        return faClock;
      default:
        return faEuroSign;
    }
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'hotovost':
        return 'text-green-600';
      case 'karta':
        return 'text-blue-600';
      case 'prevod':
        return 'text-purple-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStaffName = (staffId: string) => {
    return staff.find(s => s.id === staffId)?.name || 'Neznámý';
  };

  const getStaffRevenue = (staffId: string) => {
    return filteredRevenue.filter(r => r.staffId === staffId).reduce((sum, r) => sum + r.price, 0);
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Denní tržby" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Denní tržby</h1>
                <p className="text-gray-600 mt-1">Přehled tržeb za vybraný den</p>
              </div>
              <div className="flex items-center space-x-3">
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Daily Stats */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkové tržby</p>
                  <p className="text-2xl font-bold text-gray-900">{dailyStats.totalRevenue.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faCashRegister} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Počet služeb</p>
                  <p className="text-2xl font-bold text-gray-900">{dailyStats.totalServices}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faChartLine} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrná služba</p>
                  <p className="text-2xl font-bold text-gray-900">{dailyStats.averageService.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-orange-50 rounded-lg">
                  <FontAwesomeIcon icon={faUser} className="text-xl text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Aktivní zaměstnanci</p>
                  <p className="text-2xl font-bold text-gray-900">{[...new Set(filteredRevenue.map(r => r.staffId))].length}</p>
                </div>
              </div>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Revenue by Payment Method */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Podle způsobu platby</h3>
              <div className="space-y-4">
                {Object.entries(dailyStats.paymentMethods).map(([method, amount]) => (
                  <div key={method} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={getPaymentMethodIcon(method)} className={`w-4 h-4 mr-2 ${getPaymentMethodColor(method)}`} />
                      <span className="text-sm font-medium text-gray-900 capitalize">{method}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{amount.toLocaleString()} Kč</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue by Staff */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Podle zaměstnanců</h3>
              <div className="space-y-4">
                {staff.map(member => {
                  const revenue = getStaffRevenue(member.id);
                  const percentage = dailyStats.totalRevenue > 0 ? (revenue / dailyStats.totalRevenue) * 100 : 0;

                  return (
                    <div key={member.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{member.name}</span>
                        <span className="text-sm font-medium text-gray-900">{revenue.toLocaleString()} Kč</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${percentage}%` }}></div>
                      </div>
                      <div className="text-xs text-gray-500">{percentage.toFixed(1)}% z celku</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                  <FontAwesomeIcon icon={faCashRegister} className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Uzavřít den</span>
                </button>
                <button className="w-full flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Porovnat s včerejškem</span>
                </button>
                <button className="w-full flex items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Export denního přehledu</span>
                </button>
              </div>
            </div>
          </div>

          {/* Revenue Details */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Detail tržeb</h3>
                <div className="flex items-center space-x-3">
                  <select
                    value={filterStaff}
                    onChange={e => setFilterStaff(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  >
                    <option value="all">Všichni zaměstnanci</option>
                    {staff.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                  <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                    <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                    Filtry
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Čas</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zákazník</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaměstnanec</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platba</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRevenue.map(revenue => (
                    <tr key={revenue.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">{revenue.time}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{revenue.customer}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{revenue.service}</div>
                        <div className="text-sm text-gray-500">{revenue.duration} min</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{getStaffName(revenue.staffId)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{revenue.price.toLocaleString()} Kč</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FontAwesomeIcon
                            icon={getPaymentMethodIcon(revenue.paymentMethod)}
                            className={`w-4 h-4 mr-2 ${getPaymentMethodColor(revenue.paymentMethod)}`}
                          />
                          <span className="text-sm text-gray-900 capitalize">{revenue.paymentMethod}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit detail">
                          <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DailyRevenuePage;
